import { CompanyDetailAndJobGroups } from '@types';
import { CompanyDataProvider } from 'ogp/components/views/management/company/company-data-provider';
import { Page } from 'redesign/page/page';
import { Header } from 'redesign';
import { FormProvider, useForm } from 'react-hook-form';
import { settingsAlerts, useAlert } from 'shared/components/alerts';
import { routes } from 'ogp/Routes';
import { useNavigate } from 'react-router-dom';
import { useCompanyRelativePath } from 'ogp/hooks';
import { useTranslation } from 'react-i18next';
import { ChangeCompanySettingsPayload } from 'ogp/services/company-service.types';
import { SettingsEditToolbox } from './settings-edit-toolbox';
import { SettingsEdit } from './settings-edit';
import { useUpdateCompanySettings } from './hooks/use-update-settings';

export type CompanySettingsDataForm = {
  requiresEmployeeContract: boolean;
  requiresEmployeeCertificates: boolean;
  employeeMutableUntilHours: string;
};

export const SettingsEditPageDataProvider = () => {
  return (
    <CompanyDataProvider>
      {(companyData) => <SettingsEditPage companyData={companyData} />}
    </CompanyDataProvider>
  );
};

const SettingsEditPage = (props: { companyData: CompanyDetailAndJobGroups }) => {
  const { t } = useTranslation('ogp-screen-specific');
  const { alertSuccess, alertError } = useAlert();
  const generatePath = useCompanyRelativePath();
  const navigate = useNavigate();

  const mutation = useUpdateCompanySettings();

  const formMethods = useForm<CompanySettingsDataForm>({
    defaultValues: {
      requiresEmployeeContract: props.companyData.company.requiresEmployeeContract,
      requiresEmployeeCertificates: props.companyData.company.requiresEmployeeCertificates,
      employeeMutableUntilHours: (
        props.companyData.company.employeeMutableUntilMinutes / 60
      ).toString(),
    },
  });

  const handleSubmit = () => {
    formMethods.handleSubmit((data: CompanySettingsDataForm) =>
      mutation.mutate(mapFormToDto(data), {
        onSuccess: () => {
          // t('settings_update.success_message') - this is here to generate the success message
          alertSuccess(t(settingsAlerts.success.updated));
          navigate(generatePath(routes.Settings));
        },
        onError: (e) => alertError(e),
      })
    )();
  };

  return (
    <form>
      <FormProvider {...formMethods}>
        <Page
          toolbox={<SettingsEditToolbox handleSubmit={handleSubmit} />}
          header={<Header titleText={t('settings-edit-page.title')} />}
        >
          <SettingsEdit companyData={props.companyData.company} />
        </Page>
      </FormProvider>
    </form>
  );
};

const mapFormToDto = (data: CompanySettingsDataForm): ChangeCompanySettingsPayload => {
  return {
    requiresEmployeeContract: data.requiresEmployeeContract,
    requiresEmployeeCertificates: data.requiresEmployeeCertificates,
    employeeMutableUntilMinutes: Number(data.employeeMutableUntilHours) * 60,
  };
};
